import hierarchyData from "../commons/hierarchy.json";
import sachaHierarchyData from "../commons/sachaHierarchy.json";
import roiSummary from "../commons/roiSummary.json";

// ═══════════════════════════════════════════════════════════════
// Types
// ═══════════════════════════════════════════════════════════════

export type Modality =
  | "t1mri"
  | "t2flairmri"
  | "t2starmri"
  | "dwi"
  | "fmri"
  | "asl"
  | "pd"
  | "clinical";

export type FileRole =
  | "base" //Base image
  | "segmentation" // Segmentation mask overlay
  | "atlas" //  Atlas overlay
  | "map" //  Parameter map
  | "network" // fMRI network map
  | "mesh" // 3D mesh
  | "transform" // Spatial transformation
  | "json" // JSON data file
  | "report" //Report
  | "other";

export interface NiftiFileEntry {
  setType: string;
  directory: string;
  directoryRegex: RegExp;
  infos: string[];
  pattern: string;
  patternRegex: RegExp;
  role: FileRole;
  modality: Modality | null;
  isViewerRelevant: boolean;
  roiGroups: string[];
}

// ═══════════════════════════════════════════════════════════════
//  roiSummary mappings
// ═══════════════════════════════════════════════════════════════

function buildBvTypeToRoiGroups(): Map<string, string[]> {
  const m = new Map<string, string[]>();
  for (const [groupName, group] of Object.entries(roiSummary)) {
    const g = group as any;
    if (!g.bv_type) continue;
    if (!m.has(g.bv_type)) m.set(g.bv_type, []);
    m.get(g.bv_type)!.push(groupName);
    for (const sub of g.sub_rois ?? []) {
      if (sub.bv_type && sub.bv_type !== g.bv_type) {
        if (!m.has(sub.bv_type)) m.set(sub.bv_type, []);
        m.get(sub.bv_type)!.push(groupName);
      }
    }
  }
  return m;
}

function buildBvTypeToModality(): Map<string, Modality> {
  const m = new Map<string, Modality>();
  for (const [, group] of Object.entries(roiSummary)) {
    const g = group as any;
    if (g.bv_type && g.modality) m.set(g.bv_type, g.modality as Modality);
    for (const sub of g.sub_rois ?? []) {
      if (sub.bv_type && sub.modality)
        m.set(sub.bv_type, sub.modality as Modality);
    }
  }
  return m;
}

const BV_TYPE_TO_ROI_GROUPS = buildBvTypeToRoiGroups();
const BV_TYPE_TO_MODALITY = buildBvTypeToModality();

// ═══════════════════════════════════════════════════════════════
//  Infer FileRole
// ═══════════════════════════════════════════════════════════════

function inferRole(setType: string, directory: string): FileRole {
  const st = setType.toLowerCase();
  const dir = directory.toLowerCase();

  if (
    st.includes("spatial analysis mask") ||
    st.includes("dynamic mask") ||
    st.includes("label mask")
  ) {
    return "segmentation";
  }
  if (st.includes("bias corrected") && st.includes("flair")) {
    return "base";
  }

  if (st.startsWith("json") || st.includes("json ")) return "json";
  if (
    st.includes("pdf report") ||
    st.includes("mci pdf") ||
    st.includes("ms pdf") ||
    st.includes("pdf log") ||
    st.includes("pdf whasa") ||
    st.includes("xml ")
  )
    return "report";
  if (
    st.includes("transformation") ||
    st.includes("warp") ||
    st.includes("deformation field")
  )
    return "transform";
  if (st.includes("mesh") || dir.includes("/mesh")) return "mesh";
  if (
    st.includes("network") ||
    st.includes("precuneus") ||
    st.includes("salience") ||
    st.includes("sensorimotor") ||
    st.includes("medial temporal cortex") ||
    st.includes("medial prefrontal cortex") ||
    st.includes("hippocampus formation")
  )
    return "network";
  if (
    st.includes("mask") ||
    st.includes("lesion") ||
    st.includes("label") ||
    st.includes("segmentation")
  )
    return "segmentation";
  if (st.includes("atlas") || st.includes("aal")) return "atlas";
  if (
    st.includes("tensor") ||
    st.includes("cbf") ||
    st.includes("probamap") ||
    st.includes("probability map") ||
    st.includes("tmap") ||
    st.includes("zmap") ||
    st.includes("bsi map") ||
    st.includes("tpm") ||
    st.includes("bias field") ||
    st.includes("gradient") ||
    st.includes("histogram") ||
    st.includes("tiv file")
  )
    return "map";
  if (
    st.includes("bias corrected") ||
    st.includes("raw t1") ||
    st.includes("raw t2") ||
    st.includes("raw dwi") ||
    st.includes("raw fmri") ||
    st.includes("raw asl") ||
    st.includes("motion corrected") ||
    st.includes("distortion corrected") ||
    st.includes("intensity normalized") ||
    st.includes("downsampled") ||
    st.includes("coregistered") ||
    st.includes("apriori") ||
    st.includes("anatomical template") ||
    st.includes("ventricles template")
  )
    return "base";
  if (st.includes("snapshot") || st.includes("results")) return "report";
  if (
    st.includes("seeds") ||
    st.includes("interface") ||
    st.includes("ring") ||
    st.includes("watershed") ||
    st.includes("diffused")
  )
    return "segmentation";

  return "other";
}

// ═══════════════════════════════════════════════════════════════
//  Infer Modality
// ═══════════════════════════════════════════════════════════════

function inferModality(setType: string, directory: string): Modality | null {
  if (BV_TYPE_TO_MODALITY.has(setType))
    return BV_TYPE_TO_MODALITY.get(setType)!;
  const dir = directory.toLowerCase();
  if (dir.includes("/t1mri/") || dir.includes("/spm/")) return "t1mri";
  if (
    dir.includes("/t2flairmri/") ||
    dir.includes("/whasa_v2_transversal/") ||
    dir.includes("/whasa_4d/")
  )
    return "t2flairmri";
  if (dir.includes("/t2starmri/")) return "t2starmri";
  if (dir.includes("/dwi/")) return "dwi";
  if (dir.includes("/fmri/")) return "fmri";
  if (dir.includes("/asl/")) return "asl";
  if (dir.includes("/pd/")) return "pd";
  if (dir.includes("/clinical/")) return "clinical";
  if (
    dir.includes("/spine/") ||
    dir.includes("/basal_ganglia") ||
    dir.includes("/bgcbventbs") ||
    dir.includes("/blackholes/") ||
    dir.includes("/longitudinal/") ||
    dir.includes("/vbm_individual/") ||
    dir.includes("/toads/") ||
    dir.includes("/sacha_transversal/")
  )
    return "t1mri";
  const st = setType.toLowerCase();
  if (st.includes("flair") || st.includes("whasa") || st.includes("wmh"))
    return "t2flairmri";
  if (st.includes("t1") || st.includes("grey white") || st.includes("brain"))
    return "t1mri";
  if (st.includes("dwi") || st.includes("tensor") || st.includes("bcblab"))
    return "dwi";
  if (st.includes("fmri")) return "fmri";
  if (st.includes("asl") || st.includes("cbf")) return "asl";
  return null;
}

// ═══════════════════════════════════════════════════════════════
//  Core: build regex mapping from directory + pattern
// ═══════════════════════════════════════════════════════════════

interface HierarchyMapping {
  directory: string;
  directoryRegex: RegExp;
  infos: string[];
  pattern: string;
  patternRegex: RegExp;
}

function getMappingFromDirectoryAndPattern(
  directory: string,
  pattern: string,
): HierarchyMapping {
  const normalizedDirectory = directory
    .replace(/\{center\}\/?/, "")
    .replace(/\/+$/, "");

  const directoryRegex = new RegExp(
    normalizedDirectory.replace(/[{<]([\w\-._^]+)[}>]/gm, "([\\w\\-._^]+)") +
      "$",
  );

  const varMatches = normalizedDirectory.match(/[{<]([\w\-._^]+)[}>]/gm);
  const infos = (varMatches ?? []).map((w) =>
    w.replace(/^[{<]/, "").replace(/[}>]$/, ""),
  );

  const patternRegex = new RegExp(
    "^" + pattern.replace(/[{<]([\w\-._^]+)[}>]/gm, "([\\w\\-._^]+)"),
  );

  return {
    directory: normalizedDirectory,
    directoryRegex,
    infos,
    pattern,
    patternRegex,
  };
}

// ═══════════════════════════════════════════════════════════════
//  Recursively parse hierarchy JSON
// ═══════════════════════════════════════════════════════════════

function parseHierarchyItem(
  item: any,
  parentPath: string | null,
): Record<string, HierarchyMapping> {
  let directoryPath = parentPath;

  if (Array.isArray(item)) {
    return Object.assign(
      {},
      ...item.map((subItem) => parseHierarchyItem(subItem, parentPath)),
    );
  }

  if (item.directory) {
    directoryPath = item.directory;
  }

  let itemMapping: Record<string, HierarchyMapping> = {};

  if (item.pattern) {
    const isDirectory = item.children || item.contentMap || item.contentToApply;

    if (!isDirectory && item.setType && directoryPath != null) {
      itemMapping[item.setType] = getMappingFromDirectoryAndPattern(
        directoryPath,
        item.pattern,
      );
      directoryPath = itemMapping[item.setType].directory;
    }

    directoryPath = directoryPath
      ? directoryPath.endsWith("/")
        ? `${directoryPath}${item.pattern}`
        : `${directoryPath}/${item.pattern}`
      : item.pattern;

    if (Array.isArray(item.contentMap)) {
      itemMapping = {
        ...itemMapping,
        ...Object.assign(
          {},
          ...item.contentMap.map((c: any) =>
            parseHierarchyItem(c, directoryPath),
          ),
        ),
      };
    }
    if (item.contentToApply) {
      itemMapping = {
        ...itemMapping,
        ...parseHierarchyItem(item.contentToApply, directoryPath),
      };
    }
  }

  return itemMapping;
}

// ═══════════════════════════════════════════════════════════════
//  Build BRAINVISA_HIERARCHY + NIFTI_FILE_REGISTRY
// ═══════════════════════════════════════════════════════════════

function buildBrainVisaHierarchy(): Record<string, HierarchyMapping> {
  const allHierarchies = [
    ...(sachaHierarchyData as any[]),
    ...(hierarchyData as any[]),
  ];

  return Object.assign(
    {},
    ...allHierarchies.map((item) => parseHierarchyItem(item, null)),
  );
}

export const BRAINVISA_HIERARCHY = buildBrainVisaHierarchy();

function buildRegistry(): NiftiFileEntry[] {
  const registry: NiftiFileEntry[] = [];
  for (const [setType, mapping] of Object.entries(BRAINVISA_HIERARCHY)) {
    const role = inferRole(setType, mapping.directory);
    const modality = inferModality(setType, mapping.directory);
    const roiGroups = BV_TYPE_TO_ROI_GROUPS.get(setType) ?? [];
    const isViewerRelevant =
      roiGroups.length > 0 ||
      role === "base" ||
      role === "segmentation" ||
      role === "atlas" ||
      role === "map" ||
      role === "network";

    registry.push({
      setType,
      directory: mapping.directory,
      directoryRegex: mapping.directoryRegex,
      infos: mapping.infos,
      pattern: mapping.pattern,
      patternRegex: mapping.patternRegex,
      role,
      modality,
      isViewerRelevant,
      roiGroups,
    });
  }
  return registry;
}

export const NIFTI_FILE_REGISTRY: NiftiFileEntry[] = buildRegistry();

// ═══════════════════════════════════════════════════════════════
//  Query helpers & Debug
// ═══════════════════════════════════════════════════════════════

export const getViewerEntries = () =>
  NIFTI_FILE_REGISTRY.filter((e) => e.isViewerRelevant);
export const getEntriesByRole = (role: FileRole) =>
  NIFTI_FILE_REGISTRY.filter((e) => e.role === role);
export const getEntriesByModality = (modality: Modality) =>
  NIFTI_FILE_REGISTRY.filter((e) => e.modality === modality);
export const getEntryBySetType = (setType: string) =>
  NIFTI_FILE_REGISTRY.find((e) => e.setType === setType);
export const getBaseEntries = () =>
  NIFTI_FILE_REGISTRY.filter((e) => e.role === "base" && e.isViewerRelevant);
export const getOverlayEntries = () =>
  NIFTI_FILE_REGISTRY.filter(
    (e) =>
      (e.role === "segmentation" || e.role === "atlas") && e.isViewerRelevant,
  );

export function printRegistryStats() {
  const total = NIFTI_FILE_REGISTRY.length;
  const byRole = new Map<FileRole, number>();
  const byMod = new Map<string, number>();
  for (const e of NIFTI_FILE_REGISTRY) {
    byRole.set(e.role, (byRole.get(e.role) ?? 0) + 1);
    byMod.set(
      e.modality ?? "unknown",
      (byMod.get(e.modality ?? "unknown") ?? 0) + 1,
    );
  }
  console.table({
    "Total entries": total,
    "Viewer-relevant": NIFTI_FILE_REGISTRY.filter((e) => e.isViewerRelevant)
      .length,
    "With ROI groups": NIFTI_FILE_REGISTRY.filter((e) => e.roiGroups.length > 0)
      .length,
  });
}
